import { z } from "zod";
import type { SceneDocument } from "../scene/schema";
import type { LiveOp } from "./ops";

/**
 * Wire format of the live connection (docs/17-live-kollaboration.md).
 * Server → client: server-sent events on `GET /api/live/stream`.
 * Client → server: `POST /api/live/send`, one request at a time per connection.
 */

export const LIVE_MAX_OPS_PER_BATCH = 20_000;
/** Keep-alive comment on the event stream (proxies close idle streams after minutes). */
export const LIVE_KEEPALIVE_MS = 20_000;

const pathSegmentSchema = z.union([
  z.string().max(200),
  z.object({ id: z.string().min(1).max(200) }).strict(),
]);
const pathSchema = z.array(pathSegmentSchema).max(32);
const placeSchema = z.string().min(1).max(200).nullable();

export const liveOpSchema: z.ZodType<LiveOp> = z.union([
  z.object({ o: z.literal("set"), p: pathSchema, v: z.unknown() }).strict(),
  z.object({ o: z.literal("del"), p: pathSchema }).strict(),
  z
    .object({
      o: z.literal("ins"),
      p: pathSchema,
      id: z.string().min(1).max(200),
      a: placeSchema,
      b: placeSchema,
      v: z.unknown().optional(),
    })
    .strict(),
  z.object({ o: z.literal("rm"), p: pathSchema, id: z.string().min(1).max(200) }).strict(),
  z
    .object({
      o: z.literal("mv"),
      p: pathSchema,
      id: z.string().min(1).max(200),
      a: placeSchema,
      b: placeSchema,
    })
    .strict(),
]) as z.ZodType<LiveOp>;

export const clipRefSchema = z.object({
  track: z.enum(["video", "overlay", "audio"]),
  id: z.string().max(200),
});

/** What the others see of someone: where they are and what they have selected. */
export const livePresenceSchema = z.object({
  /** Page the person looks at. */
  page: z.string().max(200).nullable().default(null),
  /** Pointer in document pixels on that page, null when outside. */
  cursor: z.object({ x: z.number(), y: z.number() }).nullable().default(null),
  selection: z.array(z.string().max(200)).max(500).default([]),
  /** Text element the person is typing in (locked for the others). */
  editing: z.string().max(200).nullable().default(null),
  /** Video: playhead in ms and selected clip. */
  time: z.number().min(0).nullable().default(null),
  clip: clipRefSchema.nullable().default(null),
  /** AI participants: a short sentence about what they are doing right now. */
  note: z.string().max(120).nullable().default(null),
  /** AI participants: someone stopped their editing (docs/23-mcp-v2.md). */
  stopped: z.boolean().default(false),
});
export type LivePresence = z.infer<typeof livePresenceSchema>;

export const EMPTY_PRESENCE: LivePresence = {
  page: null,
  cursor: null,
  selection: [],
  editing: null,
  time: null,
  clip: null,
  note: null,
  stopped: false,
};

export const liveClientMessageSchema = z.discriminatedUnion("t", [
  z.object({
    t: z.literal("ops"),
    seq: z.number().int().positive(),
    ops: z.array(liveOpSchema).min(1).max(LIVE_MAX_OPS_PER_BATCH),
  }),
  z.object({ t: z.literal("presence"), s: livePresenceSchema }),
  /** Write the document to the database now (⌘S). */
  z.object({ t: z.literal("save") }),
  /** Stop (or release again) the editing of an AI participant (its `client`). */
  z.object({ t: z.literal("agent-stop"), client: z.string().min(1).max(200), on: z.boolean() }),
  /** Take back the last package of changes of an AI participant. */
  z.object({ t: z.literal("agent-undo"), client: z.string().min(1).max(200) }),
]);
export type LiveClientMessage = z.infer<typeof liveClientMessageSchema>;

export const liveSendSchema = z.object({
  /** Connection secret from `init`. */
  c: z.string().min(16).max(100),
  m: z.array(liveClientMessageSchema).min(1).max(200),
});

export interface LivePeer {
  /** Id of the editor session (one per browser tab). */
  client: string;
  userId: string;
  name: string;
  image: string | null;
  color: string;
  mode: "edit" | "view";
  /** An AI assistant working in the file as the person (default: a person). */
  kind?: "person" | "agent";
  s: LivePresence;
}

export type LiveServerMessage =
  | {
      t: "init";
      /** Secret for `POST /api/live/send`. */
      conn: string;
      /** Changes whenever the server loads the document anew (restart). */
      epoch: string;
      rev: number;
      /** Last revision written to the database. */
      savedRev: number;
      doc: SceneDocument;
      /** Highest batch of this editor session the server has applied (same epoch). */
      lastSeq: number;
      access: "edit" | "view";
      self: LivePeer;
      peers: LivePeer[];
    }
  /** Someone else's change, in the order the server applied it. */
  | { t: "ops"; rev: number; ops: LiveOp[]; by: string }
  /** Own batch applied. */
  | { t: "ack"; seq: number; rev: number }
  /** Own batch not applied (the merged result would be invalid). */
  | { t: "reject"; seq: number; reason: string }
  | { t: "saved"; rev: number }
  | { t: "join"; peer: LivePeer }
  | { t: "leave"; client: string }
  | { t: "presence"; client: string; s: LivePresence }
  /** Title or other file details changed. */
  | { t: "meta"; title?: string }
  /** Comments changed. */
  | { t: "comments" }
  /** A short message for the person (e.g. why taking back an AI's changes did not work). */
  | { t: "notice"; message: string }
  /** The connection ends (file in the trash, access removed). */
  | { t: "closed"; reason: string };

/** Distinct colours for the people in a file (cursors, selections, avatars). */
export const PEER_COLORS = [
  "#e5484d",
  "#f76b15",
  "#d6a100",
  "#30a46c",
  "#0d9488",
  "#0090ff",
  "#6e56cf",
  "#d6409f",
] as const;

export function peerColor(key: string): string {
  let hash = 0;
  for (let index = 0; index < key.length; index++) {
    hash = (hash * 31 + key.charCodeAt(index)) | 0;
  }
  return PEER_COLORS[Math.abs(hash) % PEER_COLORS.length]!;
}
